import React, { useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { de, enUS } from "date-fns/locale";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import { API_BASE_URL } from "../../config";
import "../../i18n";
import AppButton from "../../design-system/components/AppButton";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppChip from "../../design-system/components/AppChip";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppInput from "../../design-system/components/AppInput";
import AppSelect from "../../design-system/components/AppSelect";
import AppSwitch from "../../design-system/components/AppSwitch";
import AppTable from "../../design-system/components/AppTable";
import AppTablePane from "../../design-system/components/AppTablePane";
import AppText from "../../design-system/components/AppText";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconAdd, IconCheck, IconPreview } from "../../design-system/icons";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./projects.css";

const PROJECTS_API_URL = `${API_BASE_URL}/api/web/projects`;

const sortProjectsByStatusAndName = (rows) =>
  [...rows].sort((a, b) => {
    if (Boolean(a.active) !== Boolean(b.active)) return a.active ? -1 : 1;
    return (a.name || "").localeCompare(b.name || "");
  });

const Projects = () => {
  const { t, i18n } = useTranslation();
  const { success, error } = useToast();
  const token = localStorage.getItem("session_token");

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsProjectName, setDetailsProjectName] = useState("");
  const [detailsRows, setDetailsRows] = useState([]);
  const [detailsTotalHm, setDetailsTotalHm] = useState("00:00");
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [openCustomDetailsDialog, setOpenCustomDetailsDialog] = useState(false);
  const [customProjectRows, setCustomProjectRows] = useState([]);
  const [customProjectCount, setCustomProjectCount] = useState(0);
  const [customDetailsTotalHm, setCustomDetailsTotalHm] = useState("00:00");
  const [isCustomDetailsLoading, setIsCustomDetailsLoading] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedCustomProject, setSelectedCustomProject] = useState(null);
  const [assignTargetProject, setAssignTargetProject] = useState(null);
  const [isAssigningCustom, setIsAssigningCustom] = useState(false);
  const [assigningCustomProjectId, setAssigningCustomProjectId] = useState(null);

  const dateLocale = useMemo(() => (i18n.language === "de" ? de : enUS), [i18n.language]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const formatDateCell = (value) => {
    if (!value) return "-";
    const parsed = parseISO(value);
    if (!isValid(parsed)) return "-";
    return format(parsed, "dd.MM.yyyy", { locale: dateLocale });
  };

  const getDateSortValue = (value) => {
    if (!value) return Number.NEGATIVE_INFINITY;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed.getTime() : Number.NEGATIVE_INFINITY;
  };

  const formatMinutesHm = (minutes) => {
    const mins = Math.max(0, Number(minutes || 0));
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const formatHoursDecimal = (minutes) => {
    const hours = Math.max(0, Number(minutes || 0)) / 60;
    const locale = i18n.language === "de" ? "de-CH" : "en-US";
    return `${hours.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
  };

  const getNoteDisplay = (row) => {
    const raw = String(row?.note || "").trim();
    if (!raw) return "-";
    return raw.replace(/\n{2,}/g, "\n");
  };

  const fetchProjects = async ({ showLoader = true } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const res = await axios.get(PROJECTS_API_URL, authHeaders);
      const sortedProjects = sortProjectsByStatusAndName(res.data?.projects || []);
      setProjects(sortedProjects);
      return sortedProjects;
    } catch (err) {
      error(t("projects.toast.load_error"));
      return [];
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  const fetchCustomOverview = async ({ showLoader = false, notifyOnError = true } = {}) => {
    if (showLoader) setIsCustomDetailsLoading(true);
    try {
      const res = await axios.get(`${PROJECTS_API_URL}/custom-overview`, authHeaders);
      const rows = res.data?.rows || [];
      setCustomProjectRows(rows);
      setCustomProjectCount(Number(res.data?.custom_project_count || rows.length || 0));
      setCustomDetailsTotalHm(res.data?.total_worked_hm || "00:00");
      return res.data;
    } catch (err) {
      if (notifyOnError) {
        error(err?.response?.data?.msg || t("projects.toast.load_error"));
      }
      return null;
    } finally {
      if (showLoader) setIsCustomDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCustomOverview({ notifyOnError: false });
    // Initial load only; avoids refetch loop from unstable callback deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleActive = async (row) => {
    // Systemprojekte wie "Reisezeit" werden automatisch bebucht; inaktiv liefe
    // diese Buchung ins Leere. Das Backend weist es ohnehin ab, hier kommt der
    // Hinweis nur ohne Umweg ueber den Server.
    if (row.is_system && row.active) {
      error(t("projects.toast.system_project_cannot_be_deactivated", { name: row.name }));
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.put(
        `${PROJECTS_API_URL}/${row.id}`,
        { active: !row.active },
        authHeaders
      );
      const updated = res.data?.project;
      if (updated) {
        setProjects((prev) =>
          sortProjectsByStatusAndName(
            prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
          )
        );
      } else {
        await fetchProjects();
      }
      success(t("projects.toast.update_success"));
    } catch (err) {
      error(err?.response?.data?.msg || t("projects.toast.update_error"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async () => {
    const name = (newProjectName || "").trim();
    if (!name) {
      error(t("projects.toast.name_required"));
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(
        PROJECTS_API_URL,
        { name, active: true },
        authHeaders
      );

      const created = res.data?.project;
      if (created) {
        setProjects((prev) => sortProjectsByStatusAndName([...prev, created]));
      } else {
        await fetchProjects();
      }

      setOpenCreateDialog(false);
      setNewProjectName("");
      success(t("projects.toast.create_success"));
    } catch (err) {
      error(err?.response?.data?.msg || t("projects.toast.create_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDetails = async (row) => {
    setIsDetailsLoading(true);
    try {
      const res = await axios.get(`${PROJECTS_API_URL}/${row.id}/worklogs`, authHeaders);
      setDetailsProjectName(res.data?.project_name || row.name || "");
      setDetailsRows(res.data?.logs || []);
      setDetailsTotalHm(res.data?.total_worked_hm || row.worked_hm || "00:00");
      setOpenDetailsDialog(true);
    } catch (err) {
      error(err?.response?.data?.msg || t("projects.toast.load_error"));
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleOpenCustomDetails = async () => {
    const data = await fetchCustomOverview({ showLoader: true });
    if (data) {
      setOpenCustomDetailsDialog(true);
    }
  };

  const assignableProjects = useMemo(
    () => sortProjectsByStatusAndName(projects),
    [projects]
  );

  const handleOpenAssignDialog = (row) => {
    setSelectedCustomProject(row);
    const suggestedTarget = assignableProjects.find(
      (project) => (project.name || "").trim().toLowerCase() === (row.project_name || "").trim().toLowerCase()
    );
    setAssignTargetProject(suggestedTarget || null);
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = (force = false) => {
    if (isAssigningCustom && !force) return;
    setOpenAssignDialog(false);
    setSelectedCustomProject(null);
    setAssignTargetProject(null);
  };

  const handleAssignCustomProject = async () => {
    if (!selectedCustomProject?.project_entry_id) return;
    if (!assignTargetProject?.id) {
      error(t("projects.toast.assign_target_required"));
      return;
    }

    setIsAssigningCustom(true);
    setAssigningCustomProjectId(selectedCustomProject.project_entry_id);
    try {
      const res = await axios.post(
        `${PROJECTS_API_URL}/custom-entries/${selectedCustomProject.project_entry_id}/assign`,
        { project_id: assignTargetProject.id },
        authHeaders
      );

      await Promise.all([
        fetchProjects({ showLoader: false }),
        fetchCustomOverview({ showLoader: false, notifyOnError: false }),
      ]);

      success(
        t("projects.toast.assign_success", {
          project: selectedCustomProject.project_name,
          target: res.data?.assigned_project_name || assignTargetProject.name,
        })
      );
      handleCloseAssignDialog(true);
    } catch (err) {
      error(err?.response?.data?.msg || t("projects.toast.assign_error"));
    } finally {
      setIsAssigningCustom(false);
      setAssigningCustomProjectId(null);
    }
  };

  const columns = [
    {
      key: "name",
      label: t("projects.table.name"),
      grow: 2,
      minWidth: 160,
    },
    {
      key: "worked_hm",
      label: t("projects.table.worked"),
      width: 180,
      // Nach Minuten sortieren, nicht nach dem "HH:MM"-Text -- sonst stuende
      // "155:21" alphabetisch vor "54:10".
      sortAccessor: (row) => Number(row.worked_minutes || 0),
      render: (value, row) => (
        <div className="projects-table__worked">
          <AppText variant="body-1" className="projects-table__worked-value">
            {value || formatMinutesHm(row.worked_minutes)}
          </AppText>
          <AppTooltip content={t("projects.table.details")}>
            <AppButton variant="neutral" size="sm" iconOnly onClick={() => handleOpenDetails(row)} disabled={isDetailsLoading}>
              <IconPreview />
            </AppButton>
          </AppTooltip>
        </div>
      ),
    },
    {
      key: "creation_timestamp",
      label: t("projects.table.created_at"),
      width: 180,
      sortAccessor: (row) => getDateSortValue(row.creation_timestamp),
      render: (value) => <AppText variant="body-1">{formatDateCell(value)}</AppText>,
    },
    {
      key: "active",
      label: t("projects.table.active"),
      width: 120,
      sortable: false,
      render: (value, row) => (
        <AppSwitch checked={Boolean(value)} onChange={() => handleToggleActive(row)} disabled={isLoading} />
      ),
    },
  ];

  const detailColumns = [
    { key: "employee_name", label: t("projects.details.employee"), grow: 1, minWidth: 180 },
    {
      key: "date",
      label: t("projects.details.date"),
      width: 130,
      sortAccessor: (row) => getDateSortValue(row.date),
      render: (value) => <AppText variant="body-1">{formatDateCell(value)}</AppText>,
    },
    { key: "worked_hm", label: t("projects.details.duration"), width: 120 },
  ];

  const customDetailColumns = [
    {
      key: "project_name",
      label: t("projects.custom_details.project"),
      grow: 1,
      minWidth: 120,
      render: (value) => (
        <AppText variant="body-1" className="projects-table__multiline">
          {value || "-"}
        </AppText>
      ),
    },
    {
      key: "employee_name",
      label: t("projects.custom_details.employee"),
      width: 165,
      render: (value) => (
        <AppText variant="body-1" className="projects-table__multiline">
          {value || "-"}
        </AppText>
      ),
    },
    {
      key: "date",
      label: t("projects.custom_details.date"),
      width: 112,
      sortAccessor: (row) => getDateSortValue(row.date),
      render: (value) => <AppText variant="body-1">{formatDateCell(value)}</AppText>,
    },
    {
      key: "worked_hm",
      label: t("projects.custom_details.duration"),
      width: 80,
      sortable: false,
    },
    {
      key: "note",
      label: t("expense_report_web.table.note", "Kommentar"),
      grow: 1.8,
      minWidth: 270,
      sortable: false,
      render: (value, row) => {
        const note = getNoteDisplay(row);
        if (note === "-") return <AppText variant="body-1" tone="secondary">-</AppText>;

        return (
          <AppTooltip content={note}>
            <AppText variant="body-1">{note}</AppText>
          </AppTooltip>
        );
      },
    },
    {
      key: "assign_action",
      label: t("projects.custom_details.assign"),
      width: 172,
      sortable: false,
      align: "center",
      render: (value, row) => (
        <AppButton
          size="sm"
          variant="primary"
          onClick={() => handleOpenAssignDialog(row)}
          disabled={isAssigningCustom}
        >
          <IconCheck />
          {assigningCustomProjectId === row.project_entry_id
            ? t("projects.actions.assigning")
            : t("projects.actions.assign_project")}
        </AppButton>
      ),
    },
  ];

  const employeeWorkedData = useMemo(() => {
    const grouped = new Map();
    detailsRows.forEach((row) => {
      const key = (row.employee_name || row.email || "-").trim() || "-";
      const nextMinutes = Math.max(0, Number(row.worked_minutes || 0));
      grouped.set(key, (grouped.get(key) || 0) + nextMinutes);
    });

    const rows = Array.from(grouped.entries())
      .map(([employeeName, workedMinutes]) => ({ employeeName, workedMinutes }))
      .sort((a, b) => b.workedMinutes - a.workedMinutes);

    const maxMinutes = rows.reduce((acc, cur) => Math.max(acc, cur.workedMinutes), 0);
    return { rows, maxMinutes };
  }, [detailsRows]);

  const actions = (
    <div className="projects-toolbar__actions">
      <AppButton variant="neutral" size="md" onClick={handleOpenCustomDetails} disabled={isCustomDetailsLoading}>
        {t("projects.actions.custom_overview")}
        {customProjectCount > 0 ? <AppChip variant="error" appearance="semiFilled" size="sm">{customProjectCount}</AppChip> : null}
      </AppButton>
      <AppButton variant="primary" size="md" onClick={() => setOpenCreateDialog(true)}>
        <IconAdd />
        {t("projects.actions.add")}
      </AppButton>
    </div>
  );

  return (
    <div className="projects-page">
      <AppWorkspace
        title={t("projects.title")}
        loading={isLoading || isDetailsLoading || isCustomDetailsLoading}
      >
        <AppToolbar right={actions} />
        <AppTablePane className="projects-table-pane">
          <AppTable
            rows={projects}
            columns={columns}
            getRowId={(row) => row.id}
            emptyState={t("projects.table.no_rows")}
          />
        </AppTablePane>
      </AppWorkspace>

      <AppDialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        size="lg"
      >
        <AppDialogHeader title={t("projects.details.title", { name: detailsProjectName })} />
        <AppDialogBody className="projects-dialog__body">
          <AppText variant="body-1">
            {t("projects.details.total", { worked: detailsTotalHm })}
          </AppText>
          <AppCard className="projects-employee-card">
            <AppCardHeader
              title={t("projects.details.employee_chart_title", "Arbeitsstunden nach Mitarbeitenden")}
              titleVariant="compact"
            />
            <AppCardBody>
            {employeeWorkedData.rows.length === 0 ? (
              <AppText variant="body-2" tone="secondary">
                {t("projects.details.no_rows")}
              </AppText>
            ) : (
              <div className="projects-employee-chart">
                {employeeWorkedData.rows.map((item) => {
                  const percent = employeeWorkedData.maxMinutes > 0
                    ? (item.workedMinutes / employeeWorkedData.maxMinutes) * 100
                    : 0;
                  return (
                    <div key={item.employeeName} className="projects-employee-chart__row">
                      <AppText variant="body-2" truncate className="projects-employee-chart__name">
                        {item.employeeName}
                      </AppText>
                      <span className="projects-employee-chart__track">
                        <span
                          className="projects-employee-chart__bar"
                          style={{ width: `${Math.max(6, percent)}%` }}
                        />
                      </span>
                      <AppText variant="label-2" className="projects-employee-chart__value">
                        {formatHoursDecimal(item.workedMinutes)}
                      </AppText>
                    </div>
                  );
                })}
              </div>
            )}
            </AppCardBody>
          </AppCard>
          <AppTablePane className="projects-dialog__table">
            <AppTable
              rows={detailsRows}
              columns={detailColumns}
              getRowId={(row) => row.id}
              emptyState={t("projects.details.no_rows")}
            />
          </AppTablePane>
        </AppDialogBody>
      </AppDialog>

      <AppDialog
        open={openCreateDialog}
        onClose={() => !isSubmitting && setOpenCreateDialog(false)}
        size="sm"
      >
        <AppDialogHeader
          title={t("projects.dialog.title")}
          subtitle={t("projects.dialog.subtitle")}
        />
        <AppDialogBody>
          <AppField label={t("projects.dialog.name_label")} required>
            <AppInput
              value={newProjectName}
              onChange={(event) => setNewProjectName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (!isSubmitting) handleCreateProject();
                }
              }}
              disabled={isSubmitting}
            />
          </AppField>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="primary" onClick={handleCreateProject} disabled={isSubmitting}>
            {t("projects.actions.create")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>

      <AppDialog
        open={openCustomDetailsDialog}
        onClose={() => setOpenCustomDetailsDialog(false)}
        size="xl"
      >
        <AppDialogHeader title={t("projects.custom_details.title")} />
        <AppDialogBody className="projects-dialog__body">
          <div className="projects-summary">
            <div className="projects-summary__item">
              <AppText variant="label-2" tone="secondary">
                {t("projects.custom_details.count")}
              </AppText>
              <AppText variant="heading-4">{customProjectCount}</AppText>
            </div>
            <div className="projects-summary__item">
              <AppText variant="label-2" tone="secondary">
                {t("projects.custom_details.total_label")}
              </AppText>
              <AppText variant="heading-4">{customDetailsTotalHm}</AppText>
            </div>
          </div>
          <AppTablePane className="projects-dialog__table">
            <AppTable
              rows={customProjectRows}
              columns={customDetailColumns}
              getRowId={(row) => row.project_entry_id}
              emptyState={t("projects.custom_details.no_rows")}
            />
          </AppTablePane>
        </AppDialogBody>
      </AppDialog>

      <AppDialog
        open={openAssignDialog}
        onClose={handleCloseAssignDialog}
        size="sm"
      >
        <AppDialogHeader
          title={t("projects.assign_dialog.title")}
          subtitle={t("projects.assign_dialog.subtitle", {
            name: selectedCustomProject?.project_name || "-",
          })}
        />
        <AppDialogBody>
          <AppField
            label={t("projects.assign_dialog.target_project")}
            help={t("projects.assign_dialog.helper")}
            required
          >
            <AppSelect
              options={assignableProjects.map((project) => ({
                value: project.id,
                label: `${project.name} · ${
                  project.active
                    ? t("projects.assign_dialog.active")
                    : t("projects.assign_dialog.inactive")
                }`,
                displayLabel: project.name,
              }))}
              value={assignTargetProject?.id || ""}
              placeholder="Auswählen"
              onChange={(event) => {
                setAssignTargetProject(
                  assignableProjects.find((project) => project.id === event.target.value) || null
                );
              }}
              searchable
              searchPlaceholder={t("projects.assign_dialog.target_project")}
              disabled={isAssigningCustom}
            />
          </AppField>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton
            variant="primary"
            onClick={handleAssignCustomProject}
            disabled={isAssigningCustom || !assignTargetProject}
          >
            <IconCheck />
            {isAssigningCustom ? t("projects.actions.assigning") : t("projects.actions.assign")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>
    </div>
  );
};

export default Projects;
